import React, { useRef, useState } from 'react';
import {
	Button,
	Card,
	Empty,
	Space,
	Typography,
	notification,
	FormInstance,
	Alert,
	Collapse,
	Avatar,
	Modal,
	Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { AccessTime, MessageOutlined } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import TextConfigureForm from './TextConfigureForm';
import './TextConfiguration.scss';
import {
	createTextingCampaign,
	deleteATextingCampaign,
	editTextingCampaign,
	getAllTextingCampaigns,
} from '@/api/novi/textingCampaign';
import TextSchedule from './TextSchedule';

const { Title, Text } = Typography;

interface Campaign {
	id: string;
	campaign_id: string;
	name: string;
	message: string;
	schedule: {
		type: 'immediate' | 'delayed';
		delay?: {
			value: number;
		};
		customTime?: string;
	};
	created_at: string;
	updated_at: string;
	sent_count: number;
	delivered_count: number;
	failed_count: number;
	is_active: boolean;
}

interface TextScheduleProps {
	clientId: string;
}

const TextConfigurationInfo: React.FC<TextScheduleProps> = ({ clientId }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editCampaign, setEditCampaign] = useState<any>(null);
	const formRef = useRef<FormInstance>(null);
	const [textModal, setTextModal] = useState<boolean>(false);

	const { data, refetch } = useQuery({
		queryKey: ['textCampaigns', clientId],
		queryFn: () => getAllTextingCampaigns(clientId),
	});

	const createCampaignMutation = useMutation({
		mutationFn: createTextingCampaign,
		onSuccess: () => {
			setIsEditing(false);
			formRef.current?.resetFields();
			refetch();
			notification.success({
				message: 'Campaign Created',
				description: 'Your campaign has been created successfully.',
			});
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Failed to create campaign',
				description: error.message,
			});
		},
	});

	const deleteCampaignMutation = useMutation({
		mutationFn: (textCampaignId: string) => deleteATextingCampaign(clientId, textCampaignId),
		onSuccess: () => refetch(),
	});

	const editCampaignMutation = useMutation({
		mutationFn: editTextingCampaign,
		onSuccess: () => {
			setIsEditing(false);
			setEditCampaign(null);
			refetch();
			notification.success({
				message: 'Campaign Updated',
				description: 'Your campaign has been updated successfully.',
			});
		},
		onError: (error: Error) => {
			notification.error({
				message: 'Failed to update campaign',
				description: error.message,
			});
		},
	});

	const handleFormSubmit = (values: any) => {
		createCampaignMutation.mutate({
			campaignId: clientId,
			campaignData: values,
		});
	};

	const handleUpdateCampaign = (values: any) => {
		editCampaignMutation.mutate({
			campaignId: clientId,
			textCampaignId: editCampaign.id,
			campaignData: values,
		});
	};

	const handleEditCampaign = (textCampaign: any) => {
		setEditCampaign(textCampaign);
		setIsEditing(true);
		setTextModal(true);
	};

	const handleCancel = () => {
		setTextModal(false);
	};

	const renderEmptyState = () => (
		<Card bordered={false} className="text-center py-12">
			<Empty
				className="border border-dashed border-gray-200 p-4 bg-gray-50 rounded-lg"
				image={<MessageOutlined className="text-6xl text-blue-500" fontSize={'large'} />}
				description={
					<Space direction="vertical" size="small">
						<Title level={3}>No Text Sequence Created</Title>
						<Text type="secondary">
							Create a text sequence to send automated messages to your contacts.
						</Text>
					</Space>
				}
			>
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => {
						setTextModal(true);
						setEditCampaign({});
						setIsEditing(false);
					}}
					size="large"
					className="mt-4"
					disabled={!!(data?.length || 0 > 0)}
				>
					Create Text Campaign
				</Button>
			</Empty>
		</Card>
	);

	const renderCampaign = () => (
		<>
			<div className="flex justify-between items-center flex-wrap">
				<p>Configure your message sequence</p>
				<Button
					onClick={() => {
						setEditCampaign(null);
						setTextModal(true);
					}}
				>
					<PlusOutlined /> Add Message
				</Button>
			</div>

			{data?.map((campaign: Campaign, index: number) => (
				<div key={campaign.id} className="rounded-lg bg-gray-100 border border-gray-200 my-4">
					<div className="p-3 border-b border-gray-200 flex items-center justify-between">
						<div className="flex justify-start items-center gap-2">
							<Avatar className="bg-blue-100 text-blue-600 font-semibold">{index + 1}</Avatar>
							<p className="my-0 text-lg font-medium">{campaign.name}</p>
						</div>
						<div className="flex justify-end items-center gap-2">
							<p className="my-0 p-2 bg-white rounded-md">
								Send :{' '}
								{campaign?.schedule?.type == 'immediate'
									? 'Immediately'
									: `After ${campaign?.schedule?.delay?.value} hours`}
							</p>
							<Button type="text" onClick={() => handleEditCampaign(campaign)}>
								<EditOutlined />
							</Button>

							<Popconfirm
								title="Delete the task"
								description="Are you sure to delete this task?"
								onConfirm={() => deleteCampaignMutation.mutate(campaign.id)}
								// onCancel={cancel}
								okText="Yes"
								cancelText="No"
							>
								<Button type="text" danger>
									<DeleteOutlined />
								</Button>
							</Popconfirm>
						</div>
					</div>
					<div className="p-4">
						<div className="bg-white p-3 rounded-lg">{campaign.message}</div>
						<p className="mt-3 mb-0 text-sm">
							You can use variables like {'{first_name}'}, {'{last_name}'} etc.
						</p>
					</div>
				</div>
			))}

			<Alert message="About Message Timings" type="info" showIcon></Alert>
			{/* {data?.map((campaign: Campaign) => (
        <div key={campaign.id} className='space-y-6'>
          <div className='flex justify-between items-start'>
            <Space direction='vertical' size={1}>
              <Title level={4} className='!mb-1'>
                {campaign.name}
              </Title>
              <Space size={4}>
                {campaign.schedule && (
                  <Tag color='blue'>
                    {campaign.schedule.type === 'immediate'
                      ? 'Sends Immediately'
                      : `${campaign.schedule.delay?.value} hours delay`}
                  </Tag>
                )}
                <Tag color={campaign.is_active ? 'success' : 'error'}>
                  {campaign.is_active ? 'Active' : 'Inactive'}
                </Tag>
              </Space>
            </Space>

            <Space>
              <Button icon={<EditOutlined />} onClick={() => handleEditCampaign(campaign)} type='primary' ghost>
                Edit
              </Button>
              <Button
                type='text'
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteCampaignMutation.mutate(campaign.id)}
                loading={deleteCampaignMutation.isPending}
              />
            </Space>
          </div>

          <Card className='bg-gray-50'>
            <div
              className='prose max-w-none'
              dangerouslySetInnerHTML={{
                __html: sanitizeAndFormatText(campaign.message),
              }}
            />
          </Card>

          <div className='grid grid-cols-3 gap-4'>
            <Card className='text-center bg-gray-50'>
              <Text type='secondary' className='block mb-1'>
                Total Sent
              </Text>
              <Text strong className='text-lg'>
                {campaign.sent_count}
              </Text>
            </Card>
            <Card className='text-center bg-gray-50'>
              <Text type='secondary' className='block mb-1'>
                Delivered
              </Text>
              <Text strong className='text-lg'>
                {campaign.delivered_count}
              </Text>
            </Card>
            <Card className='text-center bg-gray-50'>
              <Text type='secondary' className='block mb-1'>
                Failed
              </Text>
              <Text strong className='text-lg'>
                {campaign.failed_count}
              </Text>
            </Card>
          </div>
        </div>
      ))} */}
		</>
	);

	// if (isEditing || (!data?.length && !editCampaign)) {
	//   return (
	//     <div>
	//       <TextConfigureForm
	//         ref={formRef}
	//         mode={editCampaign ? 'edit' : 'create'}
	//         initialValues={editCampaign}
	//         onSubmit={handleFormSubmit}
	//         onEdit={handleUpdateCampaign}
	//         showCancelBtn={!!data?.length}
	//         onCancel={() => {
	//           setIsEditing(false);
	//           setEditCampaign(null);
	//         }}
	//         isLoading={createCampaignMutation.isPending || editCampaignMutation.isPending}
	//       />
	//     </div>
	//   );
	// }

	// return <Spin spinning={isLoading}>{!data?.length ? renderEmptyState() : renderCampaign()}</Spin>;

	return (
		<>
			<div className="shadow bg-white p-4 rounded-lg">
				<h1 className="text-xl font-semibold my-2 md:my-0">Texting Agent Configurations</h1>
			</div>
			<Collapse
				className="mt-5 bg-white parent-accordion my-5 rounded-lg"
				items={[
					{
						key: '1',
						label: (
							<div className="flex items-center p-2">
								<div className="mr-3">
									<MessageOutlined className="text-green-700" />
								</div>
								<span className="text-lg font-medium text-gray-90 my-0">Message Sequence</span>
							</div>
						),
						children: !data?.length ? renderEmptyState() : renderCampaign(),
					},
				]}
				defaultActiveKey={['1']}
			/>
			<Collapse
				className="mt-5 bg-white parent-accordion mb-5 rounded-lg"
				items={[
					{
						key: '2',
						label: (
							<div className="flex items-center p-2">
								<div className="mr-3">
									<AccessTime className="text-green-700" />
								</div>
								<span className="text-lg font-medium text-gray-90 my-0">Schedule Settings</span>
							</div>
						),
						children: <TextSchedule />,
					},
				]}
			/>

			<Modal open={textModal} onCancel={handleCancel} footer={null}>
				<TextConfigureForm
					ref={formRef}
					mode={isEditing ? 'edit' : 'create'}
					initialValues={editCampaign}
					onSubmit={handleFormSubmit}
					onEdit={handleUpdateCampaign}
					showCancelBtn={!!data?.length}
					onCancel={() => {
						setIsEditing(false);
						setEditCampaign(null);
					}}
					isLoading={createCampaignMutation.isPending || editCampaignMutation.isPending}
				/>
			</Modal>
		</>
	);
};

export default TextConfigurationInfo;
